import DigitalTwinMGMOBRoute from '@/Core.Service.Domain/Controls/DigitalTwin/MGMOB/Views/DigitalTwin.vue';
import DigitalTwinFOXStudiosRoute from '@/Core.Service.Domain/Controls/DigitalTwin/FOXStudios/Views/DigitalTwin.vue';
import DigitalTwinMVPView from '@/Core.Service.Domain/Controls/DigitalTwin/DigitalTwinMVP/Views/DigitalTwinMVP.vue';
import DigitalTwinAdminIndexView from '@/Core.Service.Domain/Controls/DigitalTwin/DigitalTwinMVP/Views/DigitalTwinAdminIndex.vue';

import DynamicDigitalTwinWrapper from '@/Core.Service.Domain/Controls/DigitalTwin/Routes/DynamicDigitalTwinWrapper';

import { store } from '@/store/index.js';

import { ISite } from '@/Core.Service.Domain/ViewSupport/Portfolio/types/Site';
import { getDigitalTwinSites } from '@/Core.Service.Domain/Controls/DigitalTwin/DigitalTwinMVP/API';
import type { IDigitalTwinSite } from '@/Core.Service.Domain/Controls/DigitalTwin/DigitalTwinMVP/types';

import type { RouteLocationNormalized } from 'vue-router';

// Sites with an MVP digital twin rarely change, so fetch once per auth
// session. The pending request promise is cached (rather than its result) so
// concurrent route-guard runs share a single network call, and it is keyed to
// the access token so it cannot serve a previous user's sites after a
// logout/login without a full reload.
let mvpSitesRequest: Promise<IDigitalTwinSite[]> | null = null;
let mvpSitesRequestToken: string | null = null;

async function isMVPSite(siteId: number): Promise<boolean> {
  const token: string = store.getters['session/jwt'];

  if (!mvpSitesRequest || mvpSitesRequestToken !== token) {
    mvpSitesRequestToken = token;
    mvpSitesRequest = getDigitalTwinSites();
  }

  try {
    const mvpSites = await mvpSitesRequest;
    // The endpoint's site identifier field is ambiguous (id vs siteId), so
    // accept a match on either.
    return mvpSites.some(
      (site) => Number(site.siteId) === siteId || Number(site.id) === siteId
    );
  } catch {
    // On failure, drop the cached request so the next navigation retries, and
    // fall back to the legacy digital twin.
    mvpSitesRequest = null;
    return false;
  }
}

async function beforeEnterMVP(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
) {
  const siteId = Number(to.params.siteId);

  let userPortfolioSites =
    store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesList'];

  if (userPortfolioSites.length === 0) {
    await store.dispatch('ViewSupportStore/PortfolioStore/getPortfolioSites');

    userPortfolioSites =
      store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesList'];
  }

  const hasAccess = userPortfolioSites.some(
    (site: Partial<ISite>) => Number(site.siteId) === siteId
  );

  if (hasAccess) {
    return true;
  }

  return {
    name: 'ApplicationError',
    query: { redirectFrom: to.fullPath, originalUrl: from.fullPath },
  };
}

export const DigitalTwinMVPManagementRoute = {
  path: '/digital-twin-mvp/edit-map/:siteId',
  name: 'DigitalTwinMVPManagement',
  component: DigitalTwinMVPView,
  meta: {
    placeholder: 'Digital Twin MVP Management',
    controlTypeName: 'Digital Twin MVP',
    label: 'Digital Twin MVP Management',
    toolTip: 'Digital Twin MVP Management',
    permission: 'controls:read',
    hideControlsBar: true,
  },
  beforeEnter: beforeEnterMVP,
};

export const DigitalTwinMVPAdminIndexRoute = {
  path: '/digital-twin-mvp/admin',
  name: 'DigitalTwinMVPAdminIndex',
  component: DigitalTwinAdminIndexView,
  meta: {
    label: 'Digital Twin',
    permission: 'controls:read',
    hideControlsBar: true,
  },
};

export const DigitalTwinMVPRoute = {
  path: '/digital-twin-mvp/:siteId',
  name: 'DigitalTwinMVP',
  component: DigitalTwinMVPView,
  meta: {
    placeholder: 'Digital Twin MVP',
    controlTypeName: 'Digital Twin MVP',
    label: 'Digital Twin MVP',
    toolTip: 'Digital Twin MVP',
    permission: 'controls:read',
    hideControlsBar: true,
  },
  beforeEnter: beforeEnterMVP,
};

export const DigitalTwinRoute = {
  path: '/digital-twin/:siteId',
  name: 'DigitalTwin',
  component: DynamicDigitalTwinWrapper,
  meta: {
    placeholder: 'Search for a Digital Twin',
    controlTypeName: 'Digital Twin',
    controlTypeId: [5, 6],
    label: 'Digital Twin',
    toolTip: 'Digital Twin',
    permission: 'controls:read',
  },
  async beforeEnter(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
  ) {
    const siteIdParam = to.params.siteId;
    const siteId = Number(siteIdParam);

    let userPortfolioSites =
      store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesList'];

    if (userPortfolioSites.length === 0) {
      await store.dispatch('ViewSupportStore/PortfolioStore/getPortfolioSites');

      userPortfolioSites =
        store.getters['ViewSupportStore/PortfolioStore/getPortfolioSitesList'];
    }

    const hasAccess = userPortfolioSites.some(
      (site: Partial<ISite>) => Number(site.siteId) === siteId
      // && site.hasDigitalTwin
    );

    if (hasAccess) {
      if (await isMVPSite(siteId)) {
        return {
          name: 'DigitalTwinMVP',
          params: { siteId: siteIdParam },
        };
      }
      if (siteId === 364) {
        to.meta.dynamicComponent = DigitalTwinMGMOBRoute;
      } else if (siteId === 372) {
        to.meta.dynamicComponent = DigitalTwinFOXStudiosRoute;
      }
      return true;
    } else {
      return {
        name: 'ApplicationError',
        query: { redirectFrom: to.fullPath, originalUrl: from.fullPath },
      };
    }
  },
};
